import { useLayoutEffect, useRef } from "react";
import type { ComponentPropsWithoutRef } from "react";
import { getCachedBitmap, loadBitmap } from "./rcdataBitmapCache";

type RcdataImageProps = Omit<ComponentPropsWithoutRef<"canvas">, "children"> & {
  /** Поле RCDATA из ответа сервера: картинка в base64 без префикса `data:` */
  rcdata?: string;
  alt?: string;
};

/**
 * Показывает картинку из RCDATA на canvas вместо `<img src="data:...">`: CSP с `default-src 'self'` и без
 * `img-src data:` блокирует и data:, и blob: URL в `<img>`, а `createImageBitmap` декодирует Blob без загрузки по URL.
 *
 * Формат определяется по содержимому (GIF, PNG, JPEG, BMP), тип в RCDATA не нужен. SVG не поддерживается,
 * у анимированного GIF отображается первый кадр.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
const RcdataImage = ({ rcdata, alt, ...canvasProps }: RcdataImageProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Layout-эффект: уже декодированная картинка рисуется до отрисовки кадра, поэтому при повторном монтировании
  // (строка виртуализированного списка, смена темы) нет кадра с пустым canvas
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!rcdata || !canvas) return;
    let isCancelled = false;

    const drawBitmap = (bitmap: ImageBitmap) => {
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
    };

    const cachedBitmap = getCachedBitmap(rcdata);
    if (cachedBitmap) {
      drawBitmap(cachedBitmap);
    } else {
      loadBitmap(rcdata)
        .then((bitmap) => {
          if (!isCancelled) drawBitmap(bitmap);
        })
        .catch((error: unknown) => console.error("Не удалось отобразить картинку из RCDATA", error));
    }

    return () => {
      isCancelled = true;
    };
  }, [rcdata]);

  if (!rcdata) return null;
  return <canvas {...canvasProps} ref={canvasRef} role={alt === "" ? "presentation" : "img"} aria-label={alt} />;
};

export default RcdataImage;
